import { useMemo } from 'react';

import { RiFileSearchLine } from '@remixicon/react';

import {
  type ToolGroupEntry,
  ToolLabel,
  type ToolRenderOptions,
  createToolExtension,
} from '@spotify/plugin-confidence-chat-react';
import { useIssueRef } from '@spotify/plugin-confidence-issues-react';

import { confidenceChatPlugin } from '../plugin';
import { ToolResponseDetails } from './ToolResponseDetails';
import {
  getInputString,
  getToolInput,
  parseToolResultObject,
  truncateToolSubject,
} from './parseToolResult';

type ToolConfig = {
  running: string;
  completed: string;
  subjectKey?: string;
  subjectPrefix?: string;
  countKey?: string;
  countNoun?: string;
  summary?: 'priority' | 'updatedFields';
};

const issueTools: Record<string, ToolConfig> = {
  mcp__confidence__get_issue: {
    running: 'Reading issue',
    completed: 'Read issue',
    subjectKey: 'name',
  },
  mcp__confidence__list_issues: {
    running: 'Listing issues',
    completed: 'Listed issues',
    subjectKey: 'filter',
    subjectPrefix: 'matching',
    countKey: 'issues',
    countNoun: 'issue',
  },
  mcp__confidence__search_issues: {
    running: 'Searching issues',
    completed: 'Searched issues',
    subjectKey: 'semanticQuery',
    subjectPrefix: 'for',
    countKey: 'results',
    countNoun: 'issue',
  },
  mcp__confidence__update_issue: {
    running: 'Updating issue',
    completed: 'Updated issue',
    subjectKey: 'name',
    summary: 'updatedFields',
  },
  mcp__confidence__assess_priority: {
    running: 'Assessing priority',
    completed: 'Assessed priority',
    summary: 'priority',
  },
};

const observationTools: Record<string, ToolConfig> = {
  mcp__confidence__list_observations: {
    running: 'Listing observations',
    completed: 'Listed observations',
    subjectKey: 'filter',
    subjectPrefix: 'matching',
    countKey: 'observations',
    countNoun: 'observation',
  },
  mcp__confidence__search_observations: {
    running: 'Searching observations',
    completed: 'Searched observations',
    subjectKey: 'semanticQuery',
    subjectPrefix: 'for',
    countKey: 'results',
    countNoun: 'observation',
  },
  mcp__confidence__update_observation: {
    running: 'Updating observation',
    completed: 'Updated observation',
    subjectKey: 'name',
    summary: 'updatedFields',
  },
};

const taskTools: Record<string, ToolConfig> = {
  mcp__confidence__list_tasks: {
    running: 'Listing tasks',
    completed: 'Listed tasks',
    subjectKey: 'filter',
    subjectPrefix: 'matching',
    countKey: 'tasks',
    countNoun: 'task',
  },
};

const getSummary = (
  config: ToolConfig,
  entry: ToolGroupEntry,
  input: Record<string, unknown>,
  result: Record<string, unknown> | undefined,
): string | undefined => {
  if (!entry.result) return undefined;
  if (!entry.result.success) return 'Failed';

  if (config.countKey && config.countNoun) {
    const items = result?.[config.countKey];
    if (Array.isArray(items)) {
      return `${items.length} ${config.countNoun}${items.length === 1 ? '' : 's'}`;
    }
  }

  if (config.summary === 'updatedFields') {
    const updateMask = getInputString(input, 'updateMask');
    const fields = updateMask?.split(',').filter(field => field.trim());
    if (fields?.length) {
      return `${fields.length} ${fields.length === 1 ? 'field' : 'fields'}`;
    }
  }

  if (config.summary === 'priority') {
    const priority = result?.priority;
    if (priority && typeof priority === 'object' && 'level' in priority) {
      return typeof priority.level === 'string'
        ? `${priority.level.toLowerCase()} priority`
        : undefined;
    }
  }

  return undefined;
};

export const IssueObservationToolLabel = ({
  entry,
  options,
  config,
}: {
  entry: ToolGroupEntry;
  options: ToolRenderOptions;
  config: ToolConfig;
}) => {
  const input = getToolInput(entry.input);
  const resultContent = entry.result?.content;
  const result = useMemo(
    () =>
      entry.result?.success && resultContent
        ? parseToolResultObject(resultContent)
        : undefined,
    [entry.result?.success, resultContent],
  );
  const inputSubject = config.subjectKey
    ? getInputString(input, config.subjectKey)
    : undefined;
  const isNamedIssueTool = [
    'mcp__confidence__get_issue',
    'mcp__confidence__update_issue',
  ].includes(entry.tool);
  const { issue } = useIssueRef({
    name: isNamedIssueTool ? (inputSubject ?? '') : '',
    skip: options.isStreaming || !isNamedIssueTool || !inputSubject,
  });
  const resultName =
    entry.tool === 'mcp__confidence__get_issue' &&
    typeof result?.displayName === 'string'
      ? result.displayName
      : undefined;
  const subject = resultName ?? issue?.displayName ?? inputSubject;
  const action =
    !options.isStreaming && entry.result?.success
      ? config.completed
      : config.running;
  const summary = getSummary(config, entry, input, result);
  return (
    <ToolLabel.Root>
      <ToolLabel.Primary>
        {action}
        {subject && (!options.isStreaming || isNamedIssueTool) && (
          <>
            {' '}
            <span title={subject}>
              {config.subjectPrefix && `${config.subjectPrefix} `}
              {truncateToolSubject(subject)}
            </span>
          </>
        )}
      </ToolLabel.Primary>
      {summary && <ToolLabel.Secondary>{summary}</ToolLabel.Secondary>}
      <ToolLabel.Timer startedAt={entry.result ? undefined : entry.startedAt} />
      {entry.result && (
        <ToolLabel.Details>
          <ToolResponseDetails content={entry.result.content} />
        </ToolLabel.Details>
      )}
    </ToolLabel.Root>
  );
};

const createIssueObservationExtension = (
  id: string,
  configs: Record<string, ToolConfig>,
) =>
  confidenceChatPlugin.provide(
    createToolExtension({
      id,
      predicate: tool => Boolean(configs[tool]),
      icon: RiFileSearchLine,
      display: 'grouped',
      collapseConsecutiveCalls: false,
      render: () => null,
      renderLabel: (entry, options) => (
        <IssueObservationToolLabel
          entry={entry}
          options={options}
          config={configs[entry.tool]}
        />
      ),
    }),
  );

export const IssueToolExtension = createIssueObservationExtension(
  'issue-operations',
  issueTools,
);
export const ObservationToolExtension = createIssueObservationExtension(
  'observation-operations',
  observationTools,
);
export const TaskToolExtension = createIssueObservationExtension(
  'task-operations',
  taskTools,
);
